/**
 * TravelerStayCard + TravelerStayDeck + TravelerStayDetailModal
 * Batch 2 amendment (2026-08-07) — original TravelBio swipe-card
 * pattern for the Stay22 traveler experience on /explore.
 *
 * Card face:
 *   • Hero photo (gradient placeholder if missing)
 *   • Description line: "<name> · from $NNN <currency>"
 *   • Action row (left → right):
 *       Undo (rotate-ccw) · Skip (X) · Heart
 *   • Full-width gold Book Now button
 *
 * Tap the card body (anywhere except the action row) → expanded
 * detail modal with the full supplier list + per-supplier Book buttons.
 *
 * Backend wiring:
 *   • Heart → POST /api/bookmarks
 *       Body: {id, title, subtitle, image, provider:"stay22", vibe,
 *              payload: {property_id, supplier, price, currency,
 *                         deeplink}}
 *       Persists to `bookmarks_v2` — minimal snapshot only, per the
 *       Stay22 no-cold-storage rule. Re-hydrate live via
 *       /api/accommodations/property/{id} when the user views them.
 *   • Skip → client-side only; kept in an in-memory action stack
 *       (max 5) for Undo.
 *   • Undo → pops the stack; for a hearted card also fires
 *       DELETE /api/bookmarks/{id} to remove the bookmark.
 *   • Book Now → same delayed-open pattern used elsewhere in the
 *       Stay22 flow: window.open('about:blank') sync on click →
 *       POST /accommodations/click (context: "traveler") → set
 *       new-tab location to the returned campaign-tagged URL.
 */
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bed, RotateCcw, X as XIcon, Heart, ExternalLink,
  Loader2, Star, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EarnPointsCta from "@/components/EarnPointsCta";
import OverridePlaceholderImage from "@/components/OverridePlaceholderImage";
import AdminEditOverridePencil from "@/components/AdminEditOverridePencil";
import {
  DescriptionPreview,
  TipStack,
  FilterPillBadges,
  OverridePhotoGallery,
} from "@/components/OverrideEnrichment";

const _MAX_UNDO = 5;

function offerCoverImages(offer) {
  // Stay22 v2 sometimes includes a `media` object with either a single
  // `thumbnail` or a `gallery`/`photos` array. Adapter surfaces the
  // thumbnail as `offer.image`; we also probe common shapes for a
  // multi-image carousel in the expanded view.
  const raw = offer._raw || {};
  const gallery = raw.media?.gallery || raw.photos || raw.images
                    || raw.media?.photos;
  if (Array.isArray(gallery) && gallery.length) return gallery;
  return offer.image ? [offer.image] : [];
}


function priceLabel(offer) {
  const s = offer.supplier_offers?.[0];
  if (typeof s?.price !== "number") return null;
  return `${s.currency || "$"} ${Math.round(s.price)}`;
}


/** Fallback subtitle when the backend didn't set one — never blank. */
function offerSubtitle(offer) {
  if (offer.subtitle) return offer.subtitle;
  const loc = offer.coords ? "" : ""; // (coords are not human-readable; skip)
  const parts = [];
  if (offer.property_type) parts.push(offer.property_type);
  if (loc) parts.push(loc);
  if (parts.length === 0) parts.push("Stay");
  return parts.join(" · ");
}


async function logAndOpen(offer, supplier) {
  let tab = null;
  try { tab = window.open("about:blank", "_blank"); } catch {}
  try {
    const { data } = await api.post("/accommodations/click", {
      property_id: offer.property_id, property_name: offer.name,
      supplier: supplier.supplier, supplier_code: supplier.supplier_code,
      price: supplier.price, currency: supplier.currency,
      deeplink_used: supplier.deeplink_url, context: "traveler",
    });
    const url = data?.url || supplier.deeplink_url;
    if (tab && !tab.closed) { try { tab.location.href = url; } catch {} }
    else if (url) window.open(url, "_blank");
  } catch {
    if (tab && !tab.closed) { try { tab.location.href = supplier.deeplink_url; } catch {} }
  }
}


function bookmarkPayload(offer) {
  // Minimal-snapshot payload — TravelBio's Stay22 TOS-compliant
  // "no cold storage" contract. See BookmarksPage.jsx bookOne().
  //
  // Booking Link Override (2026-08-14) — include the resolver-attached
  // `booking_override` so the Bookmarks page can render the 2X pill +
  // byte-exact CJ href even if the resolver row is later soft-deleted.
  // The backend also re-hydrates against the live override table on
  // read, so live edits (new multiplier, new URL) propagate; the
  // snapshot is only used as a graceful fallback.
  const s = offer.supplier_offers?.[0];
  return {
    id: `stay22:${offer.property_id}`,
    title: offer.name || "",
    subtitle: offer.subtitle || null,
    image: offer.image || null,
    provider: "stay22",
    vibe: "",
    payload: {
      property_id: offer.property_id,
      supplier: s?.supplier || "Stay22",
      supplier_code: s?.supplier_code || "",
      price: typeof s?.price === "number" ? s.price : null,
      currency: s?.currency || null,
      deeplink_url: s?.deeplink_url || offer.deeplink_url || null,
    },
    booking_override: offer.booking_override || null,
  };
}


// Item B (2026-08-08) — record a skip/save in the accommodation
// swipes collection so this property doesn't reappear in future
// searches of the same location. Silent on failure — network hiccups
// shouldn't block the swipe interaction.
async function recordSwipe(offer, location, action) {
  if (!offer?.property_id || !location) return;
  try {
    await api.post("/swipes", {
      property_id: offer.property_id,
      location,
      action,   // "skip" | "save"
      property_name: offer.name || null,
      image: offer.image || null,
    });
  } catch { /* silent — the UX shouldn't block on this */ }
}


function ActionRow({ onUndo, onSkip, onHeart, undoDisabled, showUndo = true }) {
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-3">
      {showUndo ? (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onUndo(); }}
          disabled={undoDisabled}
          data-testid="stay-action-undo"
          className="w-12 h-12 rounded-full border-2 border-tb-navy/20 text-tb-navy/70 flex items-center justify-center disabled:opacity-30 hover:border-tb-navy hover:text-tb-navy tb-tap"
          aria-label="Undo"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
      ) : (
        // Spacer keeps the Skip/Heart pair right-aligned symmetric
        // when Undo is hidden (grid + detail modal contexts).
        <div className="w-12 h-12" aria-hidden="true" />
      )}
      <button type="button"
        onClick={(e) => { e.stopPropagation(); onSkip(); }}
        data-testid="stay-action-skip"
        className="w-14 h-14 rounded-full border-2 border-tb-navy/25 text-tb-navy/70 flex items-center justify-center hover:border-red-400 hover:text-red-500 tb-tap"
        aria-label="Skip"
      >
        <XIcon className="w-6 h-6" />
      </button>
      <button type="button"
        onClick={(e) => { e.stopPropagation(); onHeart(); }}
        data-testid="stay-action-heart"
        className="w-14 h-14 rounded-full border-2 border-tb-coral/50 text-tb-coral flex items-center justify-center hover:bg-tb-coral hover:text-white hover:border-tb-coral tb-tap"
        aria-label="Save"
      >
        <Heart className="w-6 h-6" />
      </button>
    </div>
  );
}


function CardFace({ offer, onOpen, onSkip, onHeart, onUndo, undoDisabled, showUndo = true }) {
  const price = priceLabel(offer);
  const stopIfAction = (e) => { /* ActionRow buttons stopPropagation themselves */ };
  // Booking Link Override (2026-08-14) — when a `property_booking_overrides`
  // row matches this property (via slug or Stay22 property_id), the
  // backend resolver rewrites `offer.deeplink_url` byte-for-byte to
  // the override target (e.g. Saratoga → CJ dpbolvw URL) and attaches
  // `offer.booking_override`. In that case the primary CTA MUST
  // render as a real `<a href>` (so the URL is visible in DOM/page
  // source) AND MUST NOT be routed through /accommodations/click —
  // that endpoint appends `?campaign=<uuid>` which would break the
  // "byte-for-byte URL, no querystring appended" contract for
  // affiliate links owned by non-Stay22 providers.
  const override = offer.booking_override || null;
  const overrideUrl = override
    ? (offer.deeplink_url || offer.supplier_offers?.[0]?.deeplink_url || "")
    : "";
  return (
    <div
      className="relative rounded-2xl bg-white border-2 border-tb-navy/15 overflow-hidden shadow-lg"
      data-testid={`stay-card-${offer.property_id}`}
    >
      {override && (
        <div className="absolute top-2 right-2 z-20">
          <AdminEditOverridePencil
            propertyKey={override.property_key}
            testId={`stay-card-edit-${offer.property_id}`}
            tone="dark"
          />
        </div>
      )}
      <button
        type="button"
        onClick={onOpen}
        onClickCapture={stopIfAction}
        data-testid={`stay-card-body-${offer.property_id}`}
        className="w-full text-left block hover:bg-tb-navy/[0.02] transition-colors"
      >
        <div className="relative aspect-[4/3] bg-white">
          {offer.image ? (
            <img src={offer.image} alt={offer.name} loading="lazy"
              className="w-full h-full object-cover" />
          ) : override ? (
            <OverridePlaceholderImage name={offer.name} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#0f2740 0%,#1a3a5f 60%,rgba(230,184,74,0.35) 100%)" }}>
              <Bed className="w-14 h-14 text-white/70" strokeWidth={1.4} />
            </div>
          )}
        </div>
        <div className="px-4 pt-3 pb-1">
          <h3
            className="text-tb-navy text-base leading-tight line-clamp-2 font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {offer.name}
          </h3>
          <div
            className="mt-1 text-tb-navy/55 font-hand text-xs line-clamp-1"
            data-testid={`stay-card-subtitle-${offer.property_id}`}
          >
            {offerSubtitle(offer)}
          </div>
          {override ? (
            <>
              <DescriptionPreview text={override.custom_description}
                                    testIdPrefix={`stay-card-${offer.property_id}`} />
              <TipStack override={override}
                         testIdPrefix={`stay-card-${offer.property_id}`} />
            </>
          ) : (
            <div className="mt-1 text-tb-navy/70 font-hand text-xs">
              {price ? `from ${price}` : "See live rates"}
            </div>
          )}
        </div>
      </button>

      <ActionRow onUndo={onUndo} onSkip={onSkip} onHeart={onHeart}
        undoDisabled={undoDisabled} showUndo={showUndo} />

      <div className="px-3 pb-3">
        {override && overrideUrl ? (
          <a
            href={overrideUrl}
            target="_blank"
            rel="noopener nofollow noreferrer"
            onClick={(e) => e.stopPropagation()}
            data-testid={`stay-book-now-${offer.property_id}`}
            data-booking-override={override.provider || "manual_override"}
            className="w-full h-11 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110 tb-tap no-underline"
          >
            Book Now <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              const s = offer.supplier_offers?.[0];
              if (s?.deeplink_url) logAndOpen(offer, s);
            }}
            data-testid={`stay-book-now-${offer.property_id}`}
            className="w-full h-11 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110 tb-tap"
          >
            Book Now <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
        <EarnPointsCta tone="light" multiplier={offer.booking_override?.rewards_multiplier ?? 1} />
      </div>
    </div>
  );
}


function DetailModal({ offer, onClose, onSkip, onHeart, onUndo, undoDisabled }) {
  const [imgIdx, setImgIdx] = useState(0);
  const images = useMemo(() => offerCoverImages(offer), [offer]);
  const suppliers = offer.supplier_offers || [];
  // Booking Link Override — see CardFace comment above for the
  // byte-for-byte contract. The DetailModal's primary CTA follows
  // the same rule.
  const override = offer.booking_override || null;
  const overrideUrl = override
    ? (offer.deeplink_url || offer.supplier_offers?.[0]?.deeplink_url || "")
    : "";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 md:p-6 overflow-y-auto"
      data-testid={`stay-detail-modal-${offer.property_id}`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl relative shadow-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button"
          onClick={onClose}
          data-testid="stay-detail-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 border-2 border-tb-navy/25 flex items-center justify-center hover:border-tb-navy"
          aria-label="Close"
        >
          <XIcon className="w-4 h-4 text-tb-navy" />
        </button>
        {override && (
          <div className="absolute top-3 right-14 z-10">
            <AdminEditOverridePencil
              propertyKey={override.property_key}
              testId={`stay-detail-edit-${offer.property_id}`}
              tone="light"
            />
          </div>
        )}

        {override && (override.photos?.length > 0) ? (
          <div className="rounded-t-3xl overflow-hidden">
            <OverridePhotoGallery photos={override.photos}
                                    name={offer.name}
                                    testIdPrefix={`stay-detail-${offer.property_id}`} />
          </div>
        ) : override ? (
          <div className="relative aspect-[16/10] rounded-t-3xl overflow-hidden">
            <OverridePlaceholderImage name={offer.name} />
          </div>
        ) : (
        <div className="relative aspect-[16/10] bg-tb-navy/5 rounded-t-3xl overflow-hidden">
          {images[imgIdx] ? (
            <img src={images[imgIdx]} alt={offer.name}
              className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#0f2740 0%,#1a3a5f 60%,rgba(230,184,74,0.35) 100%)" }}>
              <Bed className="w-16 h-16 text-white/70" />
            </div>
          )}
          {images.length > 1 && (
            <>
              <button type="button" onClick={() => setImgIdx(i => Math.max(0, i - 1))}
                data-testid="stay-detail-prev-img"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 border border-tb-navy/20 flex items-center justify-center">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setImgIdx(i => Math.min(images.length - 1, i + 1))}
                data-testid="stay-detail-next-img"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 border border-tb-navy/20 flex items-center justify-center">
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/90 text-[11px] font-hand bg-black/40 rounded-full px-2 py-0.5">
                {imgIdx + 1} / {images.length}
              </div>
            </>
          )}
        </div>
        )}

        <div className="p-5 space-y-3">
          <h2
            className="text-2xl text-tb-navy font-semibold leading-tight"
            style={{ fontFamily: '"Playfair Display", serif' }}
            data-testid="stay-detail-name"
          >
            {offer.name}
          </h2>
          {typeof offer.rating === "number" && !Number.isNaN(offer.rating) && (
            <div className="inline-flex items-center gap-1 text-tb-navy/70 text-sm font-hand">
              <Star className="w-3.5 h-3.5 fill-tb-navy/70" />
              {offer.rating.toFixed(1)}
            </div>
          )}
          {offer.property_type && (
            <div className="text-xs text-tb-navy/55 font-hand uppercase tracking-wide">
              {offer.property_type}
            </div>
          )}

          {override && override.custom_description && (
            <div data-testid={`stay-detail-desc-${offer.property_id}`}
                 className="text-sm text-tb-navy/85 whitespace-pre-wrap leading-relaxed">
              {override.custom_description}
            </div>
          )}
          {override && (
            <TipStack override={override}
                       testIdPrefix={`stay-detail-${offer.property_id}`} />
          )}

          {/* Item 2 — Rates & suppliers block:
              • ≥ 2 priced suppliers → compare row for each PRICED
                supplier only (skip null-price ones).
              • 0 or 1 priced supplier → hide the compare block; the
                Book Now button below is the single CTA. */}
          {(() => {
            const priced = suppliers.filter((s) => typeof s.price === "number");
            if (priced.length < 2) return null;
            return (
              <div className="space-y-1.5 pt-2"
                   data-testid={`stay-detail-supplier-list-${offer.property_id}`}>
                <div className="text-[10px] uppercase tracking-widest text-tb-navy/50 font-hand">
                  Compare
                </div>
                {priced.map((s) => (
                  <div key={s.supplier_code || s.supplier}
                       className="flex items-center justify-between gap-3 py-1.5 border-b border-tb-navy/10 last:border-b-0">
                    <span className="text-tb-navy font-hand text-sm">{s.supplier}</span>
                    <span className="text-tb-navy/70 font-mono text-xs whitespace-nowrap">
                      {`${s.currency || "$"} ${Math.round(s.price)}`}
                    </span>
                    <button type="button"
                      onClick={() => logAndOpen(offer, s)}
                      data-testid={`stay-detail-book-${offer.property_id}-${s.supplier_code}`}
                      className="h-8 px-3 rounded-full border-2 border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy hover:text-tb-gold-bright inline-flex items-center gap-1"
                    >
                      Book on {s.supplier}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            );
          })()}

          <ActionRow onUndo={onUndo} onSkip={onSkip} onHeart={onHeart}
            undoDisabled={undoDisabled} showUndo={false} />

          {override && overrideUrl ? (
            <a
              href={overrideUrl}
              target="_blank"
              rel="noopener nofollow noreferrer"
              data-testid={`stay-detail-book-now-${offer.property_id}`}
              data-booking-override={override.provider || "manual_override"}
              className="w-full h-11 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110 no-underline"
            >
              Book Now <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : (
            <button type="button"
              onClick={() => {
                const s = offer.supplier_offers?.[0];
                if (s?.deeplink_url) logAndOpen(offer, s);
              }}
              data-testid={`stay-detail-book-now-${offer.property_id}`}
              className="w-full h-11 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-sm font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110"
            >
              Book Now <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          <EarnPointsCta tone="light" multiplier={offer.booking_override?.rewards_multiplier ?? 1} />
          {override && (
            <FilterPillBadges
              pills={override.filter_pills}
              testId={`stay-detail-pills-${offer.property_id}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}


export function TravelerStayDeck({ offers, location }) {
  // `remaining` is the deck we haven't actioned yet — Skip/Heart pop
  // the head; Undo pushes back onto the head. This replaces the
  // previous idx-based scheme (which visually didn't advance in some
  // framer-motion race conditions and did NOT keep the grid/deck
  // state consistent for cross-view undo).
  const [remaining, setRemaining] = useState(offers);
  const [stack, setStack] = useState([]);   // undo stack: [{action, offer, bookmark_id?}]
  const [detailFor, setDetailFor] = useState(null);
  const [direction, setDirection] = useState(1);

  // A brand-new search re-mounts the deck by resetting `remaining`.
  useEffect(() => {
    setRemaining(offers);
    setStack([]);
    setDetailFor(null);
  }, [offers]);

  const current = remaining[0];

  const pushStack = (entry) =>
    setStack((s) => [...s, entry].slice(-_MAX_UNDO));

  const onSkip = useCallback(() => {
    setRemaining((r) => {
      const head = r[0];
      if (!head) return r;
      pushStack({ action: "skip", offer: head });
      setDirection(1);
      setDetailFor(null);
      // Persist skip (fire-and-forget so the UI stays snappy).
      recordSwipe(head, location, "skip");
      return r.slice(1);
    });
  }, [location]);

  const onHeart = useCallback(async () => {
    const head = remaining[0];
    if (!head) return;
    setDirection(1);
    setDetailFor(null);
    const body = bookmarkPayload(head);
    // Optimistically remove — user should see the card leave the
    // deck immediately even if the POST is slow.
    setRemaining((r) => r.slice(1));
    try {
      const { data } = await api.post("/bookmarks", body);
      pushStack({ action: "heart", offer: head,
                    bookmark_id: data?.bookmark?.id || body.id });
      // Persist save alongside the bookmark write.
      recordSwipe(head, location, "save");
      toast.success(`Saved ${head.name}`);
    } catch {
      // Roll back — put the card back on top so the user can retry.
      setRemaining((r) => [head, ...r]);
      toast.error("Couldn't save — try again.");
    }
  }, [remaining, location]);

  const onUndo = useCallback(() => {
    setStack((s) => {
      if (s.length === 0) return s;
      const last = s[s.length - 1];
      if (last.action === "heart" && last.bookmark_id) {
        api.delete(`/bookmarks/${encodeURIComponent(last.bookmark_id)}`)
          .catch(() => {});
      }
      setRemaining((r) => [last.offer, ...r]);
      setDirection(-1);
      return s.slice(0, -1);
    });
  }, []);

  if (!current) {
    return (
      <div data-testid="stay-swipe-empty" className="text-center py-12 max-w-md mx-auto">
        <p className="font-hand text-tb-navy/60">
          You&apos;ve reviewed every stay. Switch to grid to compare them again.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-md mx-auto relative" data-testid="stay-swipe-deck"
           style={{ minHeight: 480 }}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={current.property_id}
            custom={direction}
            initial={{ opacity: 0, scale: 0.94, x: direction * 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -300, rotate: direction * -6 }}
            transition={{ duration: 0.18 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={(_, info) => {
              if (info.offset.x < -100) onSkip();
              else if (info.offset.x > 100) onHeart();
            }}
          >
            <CardFace offer={current}
              onOpen={() => setDetailFor(current)}
              onSkip={onSkip} onHeart={onHeart} onUndo={onUndo}
              undoDisabled={stack.length === 0}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="text-center mt-4 font-hand text-tb-navy/50 text-xs uppercase tracking-widest"
           data-testid="stay-swipe-pos">
        {Math.max(0, offers.length - remaining.length) + 1} of {offers.length}
      </div>

      {detailFor && (
        <DetailModal offer={detailFor}
          onClose={() => setDetailFor(null)}
          onSkip={onSkip} onHeart={onHeart} onUndo={onUndo}
          undoDisabled={stack.length === 0}
        />
      )}
    </>
  );
}


export function TravelerStayGrid({ offers, location }) {
  // Mirror of TravelerStayDeck — Skip/Heart remove the card from the
  // rendered grid, Undo restores it. Kept independent so switching
  // between grid ↔ swipe on the same result set doesn't force one
  // view's state onto the other; a brand-new search resets both.
  const [remaining, setRemaining] = useState(offers);
  const [stack, setStack] = useState([]);
  const [detailFor, setDetailFor] = useState(null);

  useEffect(() => {
    setRemaining(offers);
    setStack([]);
    setDetailFor(null);
  }, [offers]);

  const pushStack = (entry) =>
    setStack((s) => [...s, entry].slice(-_MAX_UNDO));

  const skip = useCallback((o) => {
    setRemaining((r) => r.filter((x) => x.property_id !== o.property_id));
    pushStack({ action: "skip", offer: o });
    recordSwipe(o, location, "skip");
    toast.info(`Skipped ${o.name}`);
    setDetailFor(null);
  }, [location]);

  const heart = useCallback(async (o) => {
    setDetailFor(null);
    const body = bookmarkPayload(o);
    // Optimistic remove.
    setRemaining((r) => r.filter((x) => x.property_id !== o.property_id));
    try {
      const { data } = await api.post("/bookmarks", body);
      pushStack({ action: "heart", offer: o,
                    bookmark_id: data?.bookmark?.id || body.id });
      recordSwipe(o, location, "save");
      toast.success(`Saved ${o.name}`);
    } catch {
      // Roll back — add the card back at its original position (top
      // is fine; strict ordering is not a hard requirement).
      setRemaining((r) => [o, ...r]);
      toast.error("Couldn't save — try again.");
    }
  }, [location]);

  const undo = useCallback(() => {
    setStack((s) => {
      if (s.length === 0) return s;
      const last = s[s.length - 1];
      if (last.action === "heart" && last.bookmark_id) {
        api.delete(`/bookmarks/${encodeURIComponent(last.bookmark_id)}`).catch(() => {});
      }
      setRemaining((r) => [last.offer, ...r]);
      toast.info("Undone");
      return s.slice(0, -1);
    });
  }, []);

  if (remaining.length === 0) {
    return (
      <div data-testid="stay-grid-empty" className="text-center py-12 max-w-md mx-auto">
        <p className="font-hand text-tb-navy/60">
          You&apos;ve reviewed every stay. Run a new search to see more.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="stay-results-grid">
        {remaining.map((o) => (
          <CardFace key={o.property_id} offer={o}
            onOpen={() => setDetailFor(o)}
            onSkip={() => skip(o)}
            onHeart={() => heart(o)}
            onUndo={undo}
            undoDisabled={stack.length === 0}
            showUndo={false}
          />
        ))}
      </div>
      {detailFor && (
        <DetailModal offer={detailFor}
          onClose={() => setDetailFor(null)}
          onSkip={() => skip(detailFor)}
          onHeart={() => heart(detailFor)}
          onUndo={undo}
          undoDisabled={stack.length === 0}
        />
      )}
    </>
  );
}
